import {
    Mic,
    Rocket,
    Puzzle,
    RefreshCw,
    MessageCircle,
    Heart,
    AlertTriangle,
    Brain,
    Trophy,
    Star,
    Flame,
    FileText,
    ExternalLink,
    ShieldCheck,
    Users,
    HandHeart,
    PauseCircle,
    Compass,
    Smile,
} from "lucide-react";

import {
    ChecklistStrip,
    CheckList,
    SayThisCard,
    SupportCard,
    WideIntroCard,
} from "../GuideCards";

const PDF_PATH = "/Teacher_Facilitator_Guide.pdf";

const preparationTips = [
    "Ensure the game platform is open and ready to share",
    "Familiarize yourself with the categories and prompts ahead of time",
    "Test screen sharing, audio, and internet stability",
    "Encourage participants to join from a quiet, distraction-free space",
];

const environmentTips = [
    "Invite cameras on when possible to build connection",
    "Ask participants to use their real names for authenticity",
    "Set expectations for engagement: “This works best when we’re present with each other.”",
];

const agreements = [
    "One mic at a time",
    "Be present",
    "Respect every voice and experience",
    "You can always pass",
    "Confidentiality matters",
    "Be real. Be kind. Be brave.",
    "Use chat respectfully",
];

const energyTips = [
    "Keep the tone supportive, curious, and non-judgmental",
    "Encourage participants to listen deeply — not just respond",
    "Affirm honesty and vulnerability when it appears",
];

const screenTips = [
    "Speak clearly and with warmth — your tone carries more than your words",
    "Use names when calling on participants to build connection",
    "Allow extra pauses — virtual spaces need more room for people to step in",
    "Watch for non-verbal cues like facial expressions, silence, or disengagement",
];

const participationTips = [
    "Invite, don’t force",
    "Use chat as an alternative way to engage quieter participants",
    "Offer options: speaking, typing, or simply reflecting",
];

const corePrinciples = [
    "There are no right answers — only honest ones",
    "Every perspective adds value",
    "Listening is just as powerful as speaking",
];

const courageTips = [
    "Reflect back meaningful shares",
    "Highlight moments of empathy and support",
    "Acknowledge brave shares in the moment",
    "Highlight moments of insight, empathy, or accountability",
];

const emotionalMomentTips = [
    "Pause gently",
    "Normalize the experience: “This is important and real — thank you for sharing.”",
    "Offer a moment to breathe or step away if needed",
    "Offer space without forcing participation",
];

const disrespectTips = [
    "Address calmly and immediately",
    "Bring the group back to shared agreements",
    "Re-center the purpose of the experience",
    "Maintain safety without shaming",
];

const resetTips = [
    "Use a brief pause or grounding moment",
    "Invite a breath, reflection, or short reset question",
];

const reflectionQuestions = [
    "What is one insight you are taking with you?",
    "What is one action you might take after today?",
];

const nextSteps = [
    "Invite participants to continue the conversation beyond the session",
    "Share access to the Play to Heal Hub for continued engagement — www.playtohealhub.com",
    "If possible, offer follow-up sessions or deeper discussions",
];

const adaptationTips = [
    "Younger participants: simplify language and summarize prompts",
    "Larger groups: break into smaller circles with facilitators",
    "Trauma-informed settings: emphasize choice, pacing, opting out, and emotional safety",
];

const virtualAdaptations = [
    "Large groups: use breakout rooms for deeper sharing",
    "Younger participants: keep pacing tighter and instructions simpler",
    "Trauma-informed groups: emphasize choice, control, camera flexibility, and grounding pauses",
];

export default function TeacherFacilitatorGuide() {
    return (
        <>
            {/* Hero */}
            <div className="mx-auto mb-10 max-w-4xl text-center">
                <div className="mb-5 inline-flex rotate-[-1deg] items-center gap-2 border-[3px] border-black bg-[#ff3366] px-4 py-2 text-sm font-black uppercase text-white shadow-[4px_4px_0_#000]">
                    <FileText size={18} strokeWidth={3} />
                    Faculty / Counselor / Volunteer Guide
                </div>

                <h1 className="text-4xl font-black uppercase leading-none tracking-tight md:text-6xl">
                    CALL IT OUT:
                    <span className="block text-[#16bdf4] drop-shadow-[3px_3px_0_#000]">
                        Facilitator Guide
                    </span>
                </h1>

                <p className="mx-auto mt-5 max-w-3xl text-lg font-black uppercase leading-8 md:text-xl">
                    Leading Powerful Conversations That Build Courage, Empathy & Action
                </p>

                <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
                    <a
                        href={PDF_PATH}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 rounded-md border-[3px] border-black bg-[#ffbc00] px-5 py-3 text-sm font-black uppercase text-black shadow-[5px_5px_0_#000] transition hover:-translate-y-1 hover:shadow-[7px_7px_0_#000]"
                    >
                        Open PDF Directly
                        <ExternalLink size={18} strokeWidth={3} />
                    </a>

                    <a
                        href="#teacher-guide-preview"
                        className="inline-flex items-center gap-2 rounded-md border-[3px] border-black bg-white px-5 py-3 text-sm font-black uppercase text-black shadow-[5px_5px_0_#000] transition hover:-translate-y-1 hover:shadow-[7px_7px_0_#000]"
                    >
                        View Embedded Guide
                        <FileText size={18} strokeWidth={3} />
                    </a>
                </div>
            </div>

            {/* Main Guide Content */}
            <div className="mx-auto grid max-w-4xl gap-7">
                <WideIntroCard
                    icon={Mic}
                    title="Overview"
                    color="bg-[#ffbc00]"
                >
                    <p className="mb-4">
                        <strong>CALL IT OUT</strong> is a guided experience designed to help
                        participants explore bullying honestly, build empathy, and develop the
                        confidence to speak up for themselves and others.
                    </p>

                    <p className="mb-4">
                        It transforms a digital space into a place of real connection, honest
                        dialogue, and courageous action.
                    </p>

                    <p>
                        As a facilitator, your role is to <strong>bridge the distance of the screen</strong>{" "}
                        and create a space where people feel seen, heard, and safe enough to be real.
                    </p>
                </WideIntroCard>

                <div className="grid gap-6 md:grid-cols-2">
                    <ChecklistStrip
                        icon={ShieldCheck}
                        title="Tech Preparation"
                        items={preparationTips}
                        color="bg-[#16bdf4]"
                    />

                    <ChecklistStrip
                        icon={Users}
                        title="Environment & Presence"
                        items={environmentTips}
                        color="bg-[#22c55e]"
                    />
                </div>

                <WideIntroCard
                    icon={HandHeart}
                    title="Facilitator Mindset"
                    color="bg-[#a855f7]"
                >
                    <p>
                        You are not just running a session — you are{" "}
                        <strong>creating a digital space that feels human, safe, and alive.</strong>{" "}
                        You are not here to “teach” — you are here to{" "}
                        <strong>hold space, guide gently, and invite honesty.</strong>
                    </p>
                </WideIntroCard>

                <div className="grid gap-6 md:grid-cols-2">
                    <SayThisCard
                        icon={Rocket}
                        title="Opening the Experience"
                        color="bg-[#16bdf4]"
                    >
                        Today, we are stepping into a space where we can be real with each other.
                        Even though we are meeting through screens, this is a real space. CALL IT OUT
                        is about having honest conversations about bullying — how it impacts us, and
                        how we can support ourselves and each other in speaking up.
                    </SayThisCard>

                    <SayThisCard
                        icon={Star}
                        title="Invite Shared Responsibility"
                        color="bg-[#16bdf4]"
                    >
                        This works best when everyone helps protect the space. We are here to listen,
                        speak honestly, respect one another, and make room for courage.
                    </SayThisCard>
                </div>

                <ChecklistStrip
                    icon={Puzzle}
                    title="Virtual Group Agreements"
                    items={agreements}
                    color="bg-[#ff3366]"
                />

                <ChecklistStrip
                    icon={RefreshCw}
                    title="Guide the Energy"
                    items={energyTips}
                    color="bg-[#22c55e]"
                />

                <ChecklistStrip
                    icon={MessageCircle}
                    title="Facilitating Through the Screen"
                    items={screenTips}
                    color="bg-[#16bdf4]"
                />

                <div className="grid gap-6 md:grid-cols-2">
                    <ChecklistStrip
                        icon={Heart}
                        title="Encouraging Participation"
                        items={participationTips}
                        color="bg-[#ff5fa2]"
                    />

                    <ChecklistStrip
                        icon={Compass}
                        title="Reinforce Core Principles"
                        items={corePrinciples}
                        color="bg-[#ffbc00]"
                    />
                </div>

                <ChecklistStrip
                    icon={Trophy}
                    title="Celebrate Courage"
                    items={courageTips}
                    color="bg-[#a855f7]"
                />

                <SupportCard
                    icon={AlertTriangle}
                    title="Navigating Challenging Moments"
                    color="bg-[#ff6b35]"
                >
                    Difficult moments are not disruptions — they are opportunities for deeper
                    impact.
                </SupportCard>

                <div className="grid gap-6">
                    <SupportCard
                        icon={PauseCircle}
                        title="If Someone Becomes Quiet or Emotional"
                        color="bg-[#ffbc00]"
                        tips={emotionalMomentTips}
                    >
                        Pause gently, normalize the experience, and offer space without forcing
                        participation.
                    </SupportCard>

                    <SupportCard
                        icon={AlertTriangle}
                        title="If Energy Becomes Disrespectful or Off-Track"
                        color="bg-[#ff6b35]"
                        tips={disrespectTips}
                    >
                        Address the moment calmly, bring the group back to the shared agreements,
                        and maintain safety without shaming.
                    </SupportCard>
                </div>

                <SupportCard
                    icon={RefreshCw}
                    title="If the Group Needs Resetting"
                    color="bg-[#16bdf4]"
                    tips={resetTips}
                >
                    Use a brief pause, grounding moment, breath, reflection, or short reset question.
                </SupportCard>

                <WideIntroCard
                    icon={Star}
                    title="Closing the Experience"
                    color="bg-[#22c55e]"
                >
                    <p className="mb-4">
                        End with intention — this is where meaning becomes action.
                    </p>

                    <h3 className="mb-3 text-lg font-black uppercase">
                        Reflection Questions
                    </h3>

                    <CheckList items={reflectionQuestions} />
                </WideIntroCard>

                <section className="rounded-[22px] border-[4px] border-black bg-white p-5 shadow-[7px_7px_0_#000]">
                    <div className="flex flex-col gap-4 md:flex-row md:items-center">
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-[3px] border-black bg-[#ff3366] text-white shadow-[4px_4px_0_#000]">
                            <Trophy size={28} strokeWidth={3} />
                        </div>

                        <div>
                            <h2 className="mb-2 text-2xl font-black uppercase">
                                Celebrate the Group
                            </h2>

                            <p className="text-sm font-bold leading-7 md:text-base">
                                Acknowledge the courage it took to show up and participate.
                                Close with a unifying moment like a cheer, clap, or shared gesture.
                            </p>
                        </div>
                    </div>
                </section>

                <ChecklistStrip
                    icon={Rocket}
                    title="Next Steps"
                    items={nextSteps}
                    color="bg-[#16bdf4]"
                />

                <ChecklistStrip
                    icon={Brain}
                    title="Adaptations"
                    items={adaptationTips}
                    color="bg-[#ffbc00]"
                />

                <ChecklistStrip
                    icon={Smile}
                    title="Adaptations for Virtual Spaces"
                    items={virtualAdaptations}
                    color="bg-[#a855f7]"
                />
            </div>

            {/* Final Reminder */}
            <div className="mt-8 rounded-[26px] border-[5px] border-black bg-[#fff7cc] p-6 text-center shadow-none md:shadow-[9px_9px_0_#000] md:p-8">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full border-[3px] border-black bg-[#ff3366] text-white shadow-[4px_4px_0_#000]">
                    <Flame size={28} strokeWidth={3} />
                </div>

                <h2 className="mb-3 text-3xl font-black uppercase">
                    Final Thought
                </h2>

                <p className="mx-auto max-w-3xl text-base font-bold leading-8 md:text-lg">
                    Your role is powerful. When you lead with presence, compassion, and authenticity,
                    you create a space where people feel seen — and when people feel seen,{" "}
                    <strong>they begin to show up differently in the world.</strong>
                </p>

                <p className="mx-auto mt-5 max-w-3xl text-base font-bold leading-8 md:text-lg">
                    Virtual doesn’t mean distant. When held with intention, a digital space can
                    become a place where people feel{" "}
                    <strong>
                        seen across miles, heard through silence, and connected in ways that truly matter.
                    </strong>
                </p>

                <p className="mx-auto mt-5 max-w-3xl text-base font-bold leading-8 md:text-lg">
                    Hold the space. Trust the process. And let the conversations do what they were
                    meant to do — <strong>open hearts, shift perspectives, and spark real change.</strong>
                </p>
            </div>

            {/* PDF Embed */}
            <section
                id="teacher-guide-preview"
                className="mt-12 rounded-[26px] border-[5px] border-black bg-white p-4 shadow-none md:shadow-[10px_10px_0_#000] md:p-6"
            >
                <div className="mb-5 flex flex-col gap-4 border-b-[3px] border-black pb-5 md:flex-row md:items-center md:justify-between">
                    <div>
                        <h2 className="text-2xl font-black uppercase">
                            Faculty / Counselor / Volunteer Guide PDF
                        </h2>
                        <p className="mt-1 text-sm font-bold text-black/70">
                            Preview the guide here, or open the full PDF in a new tab.
                        </p>
                    </div>

                    <a
                        href={PDF_PATH}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center justify-center gap-2 rounded-md border-[3px] border-black bg-[#ff3366] px-5 py-3 text-sm font-black uppercase text-white shadow-[5px_5px_0_#000] transition hover:-translate-y-1 hover:shadow-[7px_7px_0_#000]"
                    >
                        Open PDF
                        <ExternalLink size={18} strokeWidth={3} />
                    </a>
                </div>

                <div className="h-[75vh] min-h-[560px] overflow-hidden rounded-[16px] border-[3px] border-black bg-[#e9e9eb]">
                    <iframe
                        src={PDF_PATH}
                        title="CALL IT OUT Faculty Counselor Staff Volunteer Facilitator Guide PDF"
                        className="h-full w-full"
                    />
                </div>
            </section>
        </>
    );
}